import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { SummaryDetails, TripItem } from 'src/app/model';
import { MatDialog } from '@angular/material/dialog';
import { OptimizationSummaryComponent } from '../../pop-overs/optimization-summary/optimization-summary.component';
import { TripSegmentPopoverComponent } from '../../pop-overs/trip-segment-popover/trip-segment-popover.component';
import { ServicecallService } from 'src/app';
import { ApiConstants } from 'src/app/constants/api-constants';
import { EntitiesServiceService } from 'src/app/shared/services';
import { IceOptimizationSummaryComponent } from '../../pop-overs/ice-optimization-summary/ice-optimization-summary.component';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
export interface TripSegmentData {
  battery: {
    startRange: number;
    currentRange: number;
    lowBatteryWarning?: boolean;
  };
  insights: {
    ecoPerformance?: string;
    routeOptimization?: string;
  };
  selectedTripDetails: TripItem;
  summaryData: SummaryDetails;
}
@Component({
  selector: 'app-trip-drawer',
  templateUrl: './trip-drawer.component.html',
  styleUrl: './trip-drawer.component.scss',
  animations: [
    trigger('slideInOut', [
      state('in', style({ transform: 'translateX(0)' })),
      transition('void => *', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms ease-in-out'),
      ]),
      transition('* => void', [
        animate('300ms ease-in-out', style({ transform: 'translateX(100%)' })),
      ]),
    ]),
    trigger('fadeInOut', [
      state('in', style({ opacity: 1 })),
      transition('void => *', [
        style({ opacity: 0 }),
        animate('200ms ease-in-out'),
      ]),
      transition('* => void', [
        animate('200ms ease-in-out', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class TripDrawerComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Input() summaryData;
  @Input() tripData: TripItem[] = [];
  @Input() title = 'AI Assist – Trip 0011';
  @Input() subtitle = 'Drive Recommendations';
  @Input() entityDetails: any;
  @Output() closeDrawer = new EventEmitter<void>();
  @Output() itemClick = new EventEmitter<TripItem>();
  private _selectedTrip: TripItem;
  token: any = ApiConstants.blobSasToken;
  private _tenantConfig: any;
  segmentStartLocation: string;
  segmentEndLocation: string;
  timeTaken: number;
  distanceTravelled: number;
  totalFuelConsumed: number;
  mileage: number;
  tripDetails: any;
  private _getTripDetailsSubscription: Subscription;
  constructor(private _dialog: MatDialog, private _datePipe: DatePipe, private _sharedService: ServicecallService, private _entitiesService: EntitiesServiceService) { }

  ngOnInit(): void {
    if (this.isOpen) {
      document.body.style.overflow = 'hidden';
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = 'auto';
    this._getTripDetailsSubscription?.unsubscribe();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isOpen) {
      this.onClose();
    }
  }

  onClose(): void {
    document.body.style.overflow = 'auto';
    this.closeDrawer.emit();
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  async onItemClick(item: TripItem, tripName: string, templateType?: string) {
    const selectedTrip = { ...item, id: tripName };
    this._selectedTrip = selectedTrip;
    this._getTripDetails();
  }

  emitHover(item: any | null): void {
    const selectedTrip = { ...item};
    this.itemClick.emit(selectedTrip);
  }

  trackByFn(index: number, item: TripItem): string {
    return item.tripId;
  }

  getTripLabel(index: number): string {
    return 'T' + (index + 1)?.toString()?.padStart(4, '0');
  }

  openPopover(templateType: string) {
    if (templateType === 'optimizationSummary' || (templateType === 'tripSegment' && this.entityDetails?.secondaryDetails?.fuelType !== 'EV')) {
      this._tenantConfig = this._sharedService?.getConfiguration('tenantGenericConfig')
        ? this._sharedService?.getConfiguration('tenantGenericConfig')
        : null;
      const configBasedOnCategory = this._sharedService.getConfigurationBasedOnCategory(this.summaryData?.entityDetails);
      const defaultImage = configBasedOnCategory ? configBasedOnCategory?.defaultImage : this._tenantConfig?.defaultImage;
      const defaultImageVot = this._tenantConfig?.defaultImage ? this._tenantConfig?.defaultImage?.[this.summaryData?.entityDetails?.category] : '';
      this._dialog.open<any>(this.entityDetails?.secondaryDetails?.fuelType === 'EV' ? OptimizationSummaryComponent : IceOptimizationSummaryComponent, {
        width: '60%',
        data: {
          tile1: {
            tripName: this.summaryData?.tripName ?? null,
            entityName: this.summaryData?.entityName ?? null,
            tripStartDate: (templateType === 'tripSegment' ? this._datePipe.transform(this._selectedTrip?.dataFrom, 'dd MMM yyyy hh:mm a') : this.summaryData?.tripStartDate) ?? null,
            startLocation: this.summaryData?.startLocation ?? null,
            tripEndDate: (templateType === 'tripSegment' ? this._datePipe.transform(this._selectedTrip?.dataTo, 'dd MMM yyyy hh:mm a') : this.summaryData?.tripEndDate) ?? null,
            endLocation: this.summaryData?.endLocation ?? null,
            startTime: this.summaryData?.startTime ?? null,
            endTime: this.summaryData?.endTime ?? null,
            mileage: (templateType === 'tripSegment' ? this.mileage : this.summaryData?.mileage) ?? 0,
            totalFuelConsumed: (templateType === 'tripSegment' ? this.totalFuelConsumed : this.summaryData?.totalFuelConsumed) ?? 0,
            distanceTravelled: (templateType === 'tripSegment' ? this.distanceTravelled : this.summaryData?.distanceTravelled) ?? null,
            timeTaken: (templateType === 'tripSegment' ? this.timeTaken : this.summaryData?.timeTaken) ?? 0,
            avgSpeed: this.summaryData?.tripDetails?.avgSpeed ?? null,
            topSpeed: this.summaryData?.tripDetails?.topSpeed ?? null,
            remainingRange: null,
            tags: this.summaryData?.tags,
            imageUrl: this.summaryData?.entityDetails?.image ?? (defaultImageVot ? defaultImageVot + this.token : (defaultImage ? defaultImage + this.token : this._entitiesService.getDefaultImageByCategory(this.summaryData?.entityDetails?.category))),
            entityId: this.summaryData?.entityId,
            tripId: this.summaryData?.tripId,
            selectedDate: this.summaryData?.selectedDate,
            aggregateDate: this.summaryData?.aggregateDate,
            tripType: templateType === 'tripSegment' ? 'segment' : null,
            segmentStartLocation : this.tripDetails?.from?.coordinates,
            segmentEndLocation: this.tripDetails?.to?.coordinates
          },
          ...(templateType === 'tripSegment' && this.entityDetails?.secondaryDetails?.fuelType !== 'EV'
            ? { selectedTripSegmentData: JSON.parse(JSON.stringify(this._selectedTrip)) }
            : {})
        },
        disableClose: true,
        panelClass: this.entityDetails?.secondaryDetails?.fuelType !== 'EV' ? 'ice-dialog-no-padding' : undefined
      });
    } else if (templateType === 'tripSegment') {
      const tripData: TripSegmentData = {
        battery: {
          startRange: 285,
          currentRange: 283,
          lowBatteryWarning: true,
        },
        insights: {
          ecoPerformance:
            'Your smooth acceleration saved ~4% battery this segment.',
          routeOptimization:
            'Consider Highway 101 for 8% faster travel time next trip.',
        },
        selectedTripDetails: JSON.parse(JSON.stringify(this._selectedTrip)),
        summaryData: this.summaryData
      };
      this._dialog.open(TripSegmentPopoverComponent, {
        width: '60%',
        data: tripData,
        disableClose: true,
      });
    }
  }

      /**
   * Gets trip details
   */
  private _getTripDetails() {
    const _reqPayload = {
      entityId: this.summaryData?.entityId,
      siteId: this._sharedService.getSiteId(),
      from: this._datePipe.transform(this._selectedTrip?.dataFrom,
        'yyyy-MM-ddTHH:mm:ssZZ'
      ),
      to: this._datePipe.transform(this._selectedTrip?.dataTo,
        'yyyy-MM-ddTHH:mm:ssZZ'
      ),
      isMock: false,
    };
    this._getTripDetailsSubscription = this._entitiesService
      .getEntitySummary(_reqPayload)
      .subscribe({
        next: (result: any) => {
          this.tripDetails = result;
          this.timeTaken = this._sharedService.getConfiguration('tenantGenericConfig')?.timeTakenIn === 'hr' ? (this.tripDetails?.timeTakenInMin * 3600) : (this.tripDetails?.timeTakenInMin * 60); // Converts hr/minute to second
          this.distanceTravelled = this.tripDetails?.distanceTravelled;
          this.mileage = this.tripDetails?.mileage;
          this.totalFuelConsumed = this.tripDetails?.totalFuelConsumed;
          this.openPopover('tripSegment');
        },
        error: () => {
          this.tripDetails = null;
        },
      });
  }

  async getLocationFromCoordinates(latitude: number, longitude: number): Promise<string | null> {
    try {
      const response = await fetch(
        `${ApiConstants.HERE_REVERSE_GEOCODE_URL}?at=${latitude},${longitude}&apiKey=${this._tenantConfig?.hereApiKey}&lang=en-US`
      );

      const data = await response.json();

      return data?.items?.[0]?.address?.label ?? null;

    } catch (error) {
      console.error('Failed to fetch location', error);
      return null;
    }
  }

}
